// src/App.js
import React, { useState, useEffect, useRef } from "react";
import { BrowserRouter as Router, Routes, Route, useLocation, useNavigate } from "react-router-dom";
import { UserProvider, useUser } from "./contexts/UserContext";
import { UserSettingsProvider } from "./contexts/UserSettingsContext";
import { AchievementProvider } from "./contexts/AchievementContext";
import { NotificationProvider } from "./contexts/NotificationContext";
import Sidebar from "./components/Sidebar";
import Navbar from "./components/Navbar";
import MobileChrome from "./components/MobileChrome";
import Dashboard from "./pages/Dashboard";
import Recipes from "./pages/Recipes";
import RecipeDetail from "./pages/RecipeDetail";
import Pantry from "./pages/Pantry";
import ShoppingList from "./pages/ShoppingList";
import MealPlans from "./pages/MealPlans";
import AIAssistant from "./pages/AIAssistant";
import Achievements from "./pages/Achievements";
import Leaderboard from "./pages/Leaderboard";
import Feed from "./pages/Feed";
import Profile from "./pages/Profile";
import Settings from "./pages/Settings";
import Admin from "./pages/Admin";
import SpinTheWheel from "./pages/SpinTheWheel";
import Login from "./pages/Login";
import Register from "./pages/Register";
import ResetPassword from "./pages/ResetPassword";
import TermsOfService from "./pages/TermsOfService";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import CookieConsent from "./components/CookieConsent";
import OnboardingTour from "./components/OnboardingTour";
import useViewportMode from "./hooks/useViewportMode";
import "./styles/global.css";
import "./styles/mobile/index.css";

const onboardingSteps = [
  {
    path: "/dashboard",
    icon: "\u{1F680}",
    title: "Start From Dashboard",
    description: "This is your home base. Check your level, daily bonus, quick stats, and jump into the main tools without searching through menus.",
    focus: "Use this page when you want a fast overview of your food planning progress.",
    walkthrough: [
      "Claim the daily bonus when it is available.",
      "Check the progress bar to see how close you are to the next level.",
      "Use Quick Actions to open Recipes, Pantry, Shopping, or Meal Plans.",
      "Glance at the leaderboard preview to compare progress with friends."
    ],
    functions: [
      { label: "Level card", text: "Shows your current rank, points, and next milestone." },
      { label: "Daily bonus", text: "Gives points for returning to the app." },
      { label: "Quick Actions", text: "Shortcuts to the pages you will use most." }
    ],
    tip: "Dashboard is best for deciding your next move: cook, restock, plan, or check progress."
  },
  {
    path: "/pantry",
    icon: "\u{1F96B}",
    title: "Manage Your Pantry",
    description: "Pantry is the inventory page. It tells the rest of the app what ingredients you already have, how much is left, and what may expire soon.",
    focus: "Keep this page accurate so recipes, shopping lists, and the assistant can give better suggestions.",
    walkthrough: [
      "Press Add Item to choose manual entry or quick-add ingredients.",
      "Use Scan Item when you want to read a product barcode.",
      "Update quantities with plus and minus controls after cooking or shopping.",
      "Use search, edit, and delete tools to keep old items cleaned up."
    ],
    functions: [
      { label: "Quick add", text: "Adds common ingredients in batches." },
      { label: "Scan item", text: "Finds product details from a barcode." },
      { label: "Expiry tracking", text: "Highlights food that should be used soon." },
      { label: "Bulk select", text: "Deletes several pantry items at once." }
    ],
    tip: "When pantry data is current, the recipe and shopping pages become much smarter."
  },
  {
    path: "/recipes",
    icon: "\u{1F373}",
    title: "Find Recipes",
    description: "Recipes is where you browse meals, filter by needs, save favorites, and turn missing ingredients into shopping items.",
    focus: "Use this page when you want to cook from pantry ingredients or discover something new.",
    walkthrough: [
      "Search by recipe name when you already know what you want.",
      "Use Filter for difficulty, cuisine, time, tags, and dietary preferences.",
      "Open Ingredients to select what you want to cook with.",
      "Use recipe card buttons to view details, like a recipe, or add missing ingredients to shopping."
    ],
    functions: [
      { label: "Category tabs", text: "Separate breakfast, lunch, dinner, snacks, desserts, and more." },
      { label: "Ingredient filter", text: "Finds recipes around selected ingredients." },
      { label: "Recipe cards", text: "Show time, servings, difficulty, likes, and quick actions." },
      { label: "Add recipe", text: "Lets you save your own recipe with image, steps, and nutrition." }
    ],
    tip: "Start with Ingredients when you want to use food you already have."
  },
  {
    path: "/shopping",
    icon: "\u{1F6D2}",
    title: "Shop With Lists",
    description: "Shopping keeps grocery lists organized. You can create lists, add items, check them off in the store, and send bought food back to pantry.",
    focus: "Use this page before and during shopping trips.",
    walkthrough: [
      "Create a list for a trip, recipe, or weekly restock.",
      "Add one item or multiple items to a selected list.",
      "Check items off while shopping to track what is done.",
      "Use share, print, essentials, and auto-add options when a list needs to be reused or synced."
    ],
    functions: [
      { label: "Essentials", text: "Stores recurring items you buy often." },
      { label: "Auto-add to pantry", text: "Moves completed shopping items into pantry." },
      { label: "Share and print", text: "Exports a list for another person or paper shopping." },
      { label: "Bulk select", text: "Removes several lists or items quickly." }
    ],
    tip: "Use Essentials for staples like milk, eggs, rice, and other repeat purchases."
  },
  {
    path: "/meal-plans",
    icon: "\u{1F4C5}",
    title: "Plan Meals",
    description: "Meal Plans helps you organize what you will eat across days, so cooking decisions are made before the busy part of the week.",
    focus: "Use this page to connect recipes, custom meals, and weekly structure.",
    walkthrough: [
      "Create a weekly plan or a custom plan.",
      "Move between weeks with Previous Week and Next Week.",
      "Add meals into breakfast, lunch, dinner, or snack slots.",
      "Open planned meals to review ingredients and preparation notes."
    ],
    functions: [
      { label: "Weekly Plans", text: "Calendar-style planning for the current week." },
      { label: "Custom Plans", text: "Reusable plans for routines, events, or goals." },
      { label: "Add Meal", text: "Places a meal into a specific day and slot." },
      { label: "Bulk select", text: "Deletes plans when you are cleaning up." }
    ],
    tip: "Plan around expiring pantry items first to reduce waste."
  },
  {
    path: "/assistant",
    icon: "\u{1F916}",
    title: "Ask Sous-chef",
    description: "Sous-chef is the assistant for cooking decisions. It can suggest meals, substitutions, prep ideas, and ways to use your pantry.",
    focus: "Use it when you are stuck, short on time, or trying to avoid wasting ingredients.",
    walkthrough: [
      "Choose a prompt card if you want a guided starting point.",
      "Type a specific question in the chat box.",
      "Mention time, diet, ingredients, or leftovers for better answers.",
      "Use the answer to decide whether to cook, shop, or plan next."
    ],
    functions: [
      { label: "Prompt cards", text: "Fast starters for common cooking problems." },
      { label: "Chat input", text: "Ask custom questions in your own words." },
      { label: "Pantry-aware help", text: "Uses your saved ingredients when relevant." },
      { label: "Cooldown state", text: "Prevents sending too many assistant requests too quickly." }
    ],
    tip: "Try: \"What can I cook in 20 minutes with my pantry?\""
  }
];

const BUILD_NOTICE =
  "Test build: data may reset periodically. Because the app uses free hosting, the first request after idle and occasional later requests may take longer than usual.";

function BuildNotice() {
  const noticeRef = useRef(null);

  useEffect(() => {
    const notice = noticeRef.current;
    if (!notice) return undefined;

    const updateNoticeHeight = () => {
      const nextHeight = Math.ceil(notice.getBoundingClientRect().height);
      document.documentElement.style.setProperty("--build-notice-height", `${nextHeight}px`);
    };

    updateNoticeHeight();

    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", updateNoticeHeight);
      return () => window.removeEventListener("resize", updateNoticeHeight);
    }

    const observer = new ResizeObserver(updateNoticeHeight);
    observer.observe(notice);
    window.addEventListener("resize", updateNoticeHeight);

    return () => {
      observer.disconnect();
      window.removeEventListener("resize", updateNoticeHeight);
    };
  }, []);

  return (
    <div className="build-notice-bar" role="status" ref={noticeRef}>
      <strong>Notice:</strong>
      <span>{BUILD_NOTICE}</span>
    </div>
  );
}

function AppContent() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [onboardingActive, setOnboardingActive] = useState(false);
  const [onboardingStepIndex, setOnboardingStepIndex] = useState(0);
  const [authPrompt, setAuthPrompt] = useState({ open: false, message: "" });
  const { isAuthenticated, user } = useUser();
  const { viewportMode, isTouchLayout } = useViewportMode();
  const location = useLocation();
  const navigate = useNavigate();

  const publicStandaloneRoutes = ["/login", "/register", "/reset-password", "/terms", "/privacy"];
  const isProtectedRoute = !publicStandaloneRoutes.includes(location.pathname);
  const isAuthPage = ["/login", "/register"].includes(location.pathname);
  const hideAppChrome = publicStandaloneRoutes.includes(location.pathname);

  // Close sidebar when route changes
  useEffect(() => {
    setSidebarOpen(false);
  }, [location]);

  useEffect(() => {
    document.body.classList.toggle("mobile-nav-open", Boolean(sidebarOpen && isTouchLayout));

    return () => {
      document.body.classList.remove("mobile-nav-open");
    };
  }, [sidebarOpen, isTouchLayout]);

  useEffect(() => {
    document.body.classList.remove("ui-mobile", "ui-tablet", "ui-desktop", "ui-touch");
    document.body.classList.add(`ui-${viewportMode}`);
    if (isTouchLayout) {
      document.body.classList.add("ui-touch");
    }

    return () => {
      document.body.classList.remove("ui-mobile", "ui-tablet", "ui-desktop", "ui-touch");
    };
  }, [viewportMode, isTouchLayout]);

  useEffect(() => {
    const handleAuthPrompt = (event) => {
      if (hideAppChrome) {
        return;
      }

      setAuthPrompt({
        open: true,
        message: event.detail?.message || "Create a free account or log in to use this feature."
      });
    };

    window.addEventListener("prepRightAuthPrompt", handleAuthPrompt);
    return () => window.removeEventListener("prepRightAuthPrompt", handleAuthPrompt);
  }, [hideAppChrome]);

  useEffect(() => {
    if (hideAppChrome) {
      setAuthPrompt({ open: false, message: "" });
    }
  }, [hideAppChrome]);

  // First-time flow:
  // 1) Open pantry quick start modal
  // 2) Start onboarding tutorial after user adds items or skips quick start
  useEffect(() => {
    if (!isAuthenticated || !user || !isProtectedRoute || onboardingActive) return;

    const userKey = user.id || user.email || "user";
    const onboardingKey = `prep_right_onboarding_done_${userKey}`;
    const quickStartKey = `prep_right_quick_start_done_${userKey}`;
    const onboardingReadyKey = `prep_right_onboarding_ready_${userKey}`;

    const onboardingAlreadyDone = localStorage.getItem(onboardingKey) === "true";
    if (onboardingAlreadyDone) return;

    const quickStartDone = localStorage.getItem(quickStartKey) === "true";
    if (!quickStartDone) {
      if (!(location.pathname === "/pantry" && location.search.includes("quickstart=1"))) {
        navigate("/pantry?quickstart=1", { replace: true });
      }
      return;
    }

    // Backward compatibility: if quick start is done but ready flag is missing, allow onboarding.
    let onboardingReady = localStorage.getItem(onboardingReadyKey) === "true";
    if (!onboardingReady) {
      localStorage.setItem(onboardingReadyKey, "true");
      onboardingReady = true;
    }

    if (onboardingReady) {
      setOnboardingStepIndex(0);
      setOnboardingActive(true);
      if (location.pathname !== onboardingSteps[0].path) {
        navigate(onboardingSteps[0].path);
      }
    }
  }, [isAuthenticated, user, isProtectedRoute, onboardingActive, location.pathname, location.search, navigate]);

  useEffect(() => {
    if (!isAuthenticated) return;

    const handleStartOnboarding = () => {
      setOnboardingStepIndex(0);
      setOnboardingActive(true);
      if (location.pathname !== onboardingSteps[0].path) {
        navigate(onboardingSteps[0].path);
      }
    };

    window.addEventListener("startOnboardingTour", handleStartOnboarding);
    return () => {
      window.removeEventListener("startOnboardingTour", handleStartOnboarding);
    };
  }, [isAuthenticated, location.pathname, navigate]);

  // Keep tooltip content in sync if user navigates manually during tour.
  useEffect(() => {
    if (!onboardingActive) return;
    const foundStepIndex = onboardingSteps.findIndex((step) => step.path === location.pathname);
    if (foundStepIndex >= 0 && foundStepIndex !== onboardingStepIndex) {
      setOnboardingStepIndex(foundStepIndex);
    }
  }, [onboardingActive, onboardingStepIndex, location.pathname]);

  const completeOnboarding = () => {
    if (user) {
      const onboardingKey = `prep_right_onboarding_done_${user.id || user.email || "user"}`;
      localStorage.setItem(onboardingKey, "true");
    }
    setOnboardingActive(false);
  };

  const handleOnboardingNext = () => {
    if (onboardingStepIndex >= onboardingSteps.length - 1) {
      completeOnboarding();
      return;
    }
    const nextIndex = onboardingStepIndex + 1;
    setOnboardingStepIndex(nextIndex);
    navigate(onboardingSteps[nextIndex].path);
  };

  const handleOnboardingBack = () => {
    if (onboardingStepIndex <= 0) return;
    const previousIndex = onboardingStepIndex - 1;
    setOnboardingStepIndex(previousIndex);
    navigate(onboardingSteps[previousIndex].path);
  };

  if (isAuthenticated && isAuthPage) {
    return <Dashboard sidebarOpen={sidebarOpen} />;
  }

  return (
    <div className={`app-container ${sidebarOpen ? "sidebar-open" : ""} ${hideAppChrome ? "app-container--standalone" : ""}`}>
      {!isTouchLayout && sidebarOpen && <Sidebar />}
      {!isTouchLayout && sidebarOpen && (
        <button
          type="button"
          className="app-sidebar-backdrop"
          aria-label="Close navigation menu"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      <div className={`main-content ${sidebarOpen ? "sidebar-open" : ""} ${hideAppChrome ? "main-content--standalone" : ""}`}>
        {!isTouchLayout && !hideAppChrome && <Navbar onToggleSidebar={() => setSidebarOpen((s) => !s)} />}
        {isTouchLayout && !hideAppChrome && (
          <MobileChrome
            isOpen={sidebarOpen}
            onToggleMenu={() => setSidebarOpen((s) => !s)}
            onCloseMenu={() => setSidebarOpen(false)}
          />
        )}

        <Routes>
          <Route path="/" element={<Dashboard sidebarOpen={sidebarOpen} />} />
          <Route path="/dashboard" element={<Dashboard sidebarOpen={sidebarOpen} />} />
          <Route path="/recipes" element={<Recipes sidebarOpen={sidebarOpen} />} />
          <Route path="/recipes/:id" element={<RecipeDetail sidebarOpen={sidebarOpen} />} />
          <Route path="/pantry" element={<Pantry sidebarOpen={sidebarOpen} />} />
          <Route path="/shopping" element={<ShoppingList sidebarOpen={sidebarOpen} />} />
          <Route path="/meal-plans" element={<MealPlans sidebarOpen={sidebarOpen} />} />
          <Route path="/assistant" element={<AIAssistant sidebarOpen={sidebarOpen} />} />
          <Route path="/achievements" element={<Achievements sidebarOpen={sidebarOpen} />} />
          <Route path="/leaderboard" element={<Leaderboard sidebarOpen={sidebarOpen} />} />
          <Route path="/feed" element={<Feed sidebarOpen={sidebarOpen} />} />
          <Route path="/profile" element={<Profile sidebarOpen={sidebarOpen} />} />
          <Route path="/settings" element={<Settings sidebarOpen={sidebarOpen} />} />
          <Route path="/spin-wheel" element={<SpinTheWheel sidebarOpen={sidebarOpen} />} />
          <Route path="/admin" element={<Admin />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/terms" element={<TermsOfService sidebarOpen={sidebarOpen} />} />
          <Route path="/privacy" element={<PrivacyPolicy sidebarOpen={sidebarOpen} />} />
        </Routes>

        {isAuthenticated && onboardingActive && (
          <OnboardingTour
            step={onboardingSteps[onboardingStepIndex]}
            stepNumber={onboardingStepIndex + 1}
            totalSteps={onboardingSteps.length}
            onNext={handleOnboardingNext}
            onBack={handleOnboardingBack}
            onSkip={completeOnboarding}
            isFirstStep={onboardingStepIndex === 0}
            isLastStep={onboardingStepIndex === onboardingSteps.length - 1}
          />
        )}

        {authPrompt.open && (
          <div className="auth-prompt-overlay" onClick={() => setAuthPrompt({ open: false, message: "" })}>
            <div className="auth-prompt-modal" onClick={(event) => event.stopPropagation()}>
              <button
                type="button"
                className="auth-prompt-close"
                onClick={() => setAuthPrompt({ open: false, message: "" })}
                aria-label="Close"
              >
                x
              </button>
              <h2>Join PrepRight</h2>
              <p>{authPrompt.message}</p>
              <div className="auth-prompt-actions">
                <button
                  type="button"
                  className="auth-prompt-primary"
                  onClick={() => {
                    setAuthPrompt({ open: false, message: "" });
                    navigate("/register");
                  }}
                >
                  Register
                </button>
                <button
                  type="button"
                  className="auth-prompt-secondary"
                  onClick={() => {
                    setAuthPrompt({ open: false, message: "" });
                    navigate("/login");
                  }}
                >
                  Log in
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function App() {
  return (
    <UserProvider>
      <UserSettingsProvider>
        <AchievementProvider>
          <NotificationProvider>
            <Router>
              <BuildNotice />
              <AppContent />
              <CookieConsent />
            </Router>
          </NotificationProvider>
        </AchievementProvider>
      </UserSettingsProvider>
    </UserProvider>
  );
}

export default App;
